/**
 * The small creatures that make a place feel lived in (data/folk.ts `CRITTERS`): hens pecking
 * about the houses, cats dozing by the inn, sparrows hopping in the square that take wing when
 * someone comes near. Drawn facing screen-right, like the other beasts.
 */
import { figOutline } from './figureOutline';
import { lazyRecord } from './drawBudget';
import { propSprite } from './kit/props';
import { box, canvas, ctx2d, ell } from './pixel';
import { FRAMES, type SpriteSet } from './sprites';

type Frame = HTMLCanvasElement;

function set(idle: Frame[], walk: Frame[], ax: number, ay: number, h: number, fly?: Frame[]): SpriteSet {
  return {
    idle,
    walk,
    atk: walk[0]!,
    block: idle[0]!,
    cast: idle[0]!,
    sit: idle[0]!,
    jump: fly?.[0] ?? walk[0]!,
    poses: {},
    ax,
    ay,
    h,
    ...(fly ? { fly } : {}),
  };
}

/**
 * A hen: `peck` (0–1) how far the head has dipped toward the ground; `step` (0–4 round a stride)
 * lifts one foot, then the other.
 */
function hen(peck: number, step: number): Frame {
  const c = canvas(16, 15);
  const g = ctx2d(c);
  const body = '#f2ead8';
  const shade = '#c8bca4';
  const lift = Math.round(Math.max(0, Math.sin((step * Math.PI) / 2)));
  const lift2 = Math.round(Math.max(0, -Math.sin((step * Math.PI) / 2)));
  // Legs.
  box(g, 6, 11 - lift, 1, 3, '#e0a030');
  box(g, 9, 11 - lift2, 1, 3, '#e0a030');
  box(g, 5, 13 - lift, 2, 1, '#e0a030');
  box(g, 8, 13 - lift2, 2, 1, '#e0a030');
  // Body and tail.
  ell(g, 7.5, 8, 5, 3.5, body);
  ell(g, 7, 9.5, 4, 1.6, shade);
  box(g, 2, 3, 2, 4, body);
  box(g, 1, 2, 2, 3, shade);
  ell(g, 7, 7, 2.5, 1.5, shade);
  // Head: up, or down at the ground.
  const hx = Math.round(12 + peck);
  const hy = Math.round(4 + 5 * peck);
  ell(g, hx, hy, 2, 2, body);
  box(g, hx - 1, hy - 3, 2, 1, '#d8302a');
  box(g, hx, hy - 3, 1, 2, '#d8302a');
  box(g, hx + 2, hy, 2, 1, '#e0a030');
  box(g, hx + 1, hy + 1, 1, 2, '#d8302a');
  box(g, hx, hy - 1, 1, 1, '#141010');
  if (peck < 0.5) box(g, 11, 6, 2, 2, body);
  return figOutline(c);
}

/** A cat: sitting tall, curled up asleep, or walking (`step` 0–4 round a stride). */
function cat(pose: 'sit' | 'sleep' | 'walk', step = 0): Frame {
  const c = canvas(22, 16);
  const g = ctx2d(c);
  const fur = '#d8954a';
  const dark = '#a8682a';
  const lite = '#f2c890';
  if (pose === 'sleep') {
    ell(g, 11, 11, 7, 3.5, fur);
    ell(g, 11, 12.5, 6, 1.6, dark);
    ell(g, 16, 10, 3, 2.5, fur);
    box(g, 15, 7, 1, 2, fur);
    box(g, 18, 7, 1, 2, fur);
    box(g, 16, 10, 2, 1, '#3a2418');
    box(g, 3, 11, 5, 2, fur);
    box(g, 4, 12, 3, 1, dark);
    for (const x of [8, 11, 14]) box(g, x, 9, 1, 2, dark);
    return figOutline(c);
  }
  if (pose === 'sit') {
    ell(g, 10, 10, 4, 4.5, fur);
    ell(g, 11, 11, 2.5, 3, lite);
    box(g, 8, 14, 2, 1, fur);
    box(g, 11, 14, 2, 1, fur);
    // Tail curled round the feet.
    box(g, 3, 13, 6, 2, fur);
    box(g, 3, 11, 2, 2, fur);
    ell(g, 12, 4.5, 3.2, 3, fur);
    box(g, 9, 0, 2, 3, fur);
    box(g, 13, 0, 2, 3, fur);
    box(g, 10, 1, 1, 1, '#f0a0a0');
    box(g, 11, 4, 1, 1, '#2a8a3a');
    box(g, 14, 4, 1, 1, '#2a8a3a');
    box(g, 13, 6, 1, 1, '#c86a6a');
    for (const y of [8, 10]) box(g, 7, y, 1, 1, dark);
    return figOutline(c);
  }
  // Walking: legs in a four-beat gait, the tail held up.
  const a = Math.round(Math.sin((step * Math.PI) / 2));
  box(g, 6 + a, 11, 2, 4, dark);
  box(g, 14 - a, 11, 2, 4, dark);
  ell(g, 11, 9, 6.5, 3, fur);
  box(g, 7 - a, 11, 2, 4, fur);
  box(g, 15 + a, 11, 2, 4, fur);
  for (const x of [8, 11, 14]) box(g, x, 6, 1, 2, dark);
  box(g, 3, 4, 2, 5, fur);
  box(g, 2, 2, 2, 3, fur);
  ell(g, 18, 6, 3, 2.6, fur);
  box(g, 16, 2, 2, 2, fur);
  box(g, 19, 2, 2, 2, fur);
  box(g, 19, 5, 1, 1, '#2a8a3a');
  box(g, 20, 7, 1, 1, '#c86a6a');
  return figOutline(c);
}

/** A sparrow on the ground: `hop` (0–1) off the ground, `peck` (0–1) the head dipped. */
function sparrow(hop: number, peck: number): Frame {
  const c = canvas(10, 9);
  const g = ctx2d(c);
  const y = -Math.round(hop);
  box(g, 4, 7 + y, 1, 1, '#5a4030');
  box(g, 6, 7 + y, 1, 1, '#5a4030');
  ell(g, 5, 5 + y, 3, 2, '#8a6440');
  ell(g, 5, 6 + y, 2, 1, '#e8dcc0');
  box(g, 1, 4 + y, 2, 1, '#5a4030');
  const hx = Math.round(7 + peck);
  const hy = Math.round(3 + 3 * peck);
  ell(g, hx, hy + y, 1.6, 1.4, '#6a4a30');
  box(g, hx + 1, hy + y, 1, 1, '#2a2018');
  box(g, hx, hy - 1 + y, 1, 1, '#141010');
  return figOutline(c);
}

/** A sparrow in flight: wings up, level, down and back (`beat` 0–4 round a wingbeat). */
function sparrowFlying(beat: number): Frame {
  const c = canvas(14, 10);
  const g = ctx2d(c);
  ell(g, 7, 5, 3, 1.6, '#8a6440');
  box(g, 2, 5, 2, 1, '#5a4030');
  box(g, 10, 4, 2, 2, '#6a4a30');
  box(g, 12, 5, 1, 1, '#2a2018');
  const wy = Math.round(3 - 3 * Math.cos((beat * Math.PI) / 2));
  box(g, 5, Math.min(wy, 4), 3, Math.abs(4 - wy) + 1, '#5a4030');
  box(g, 6, wy, 2, 1, '#a8845a');
  return figOutline(c);
}

/** `FRAMES` frames round a cycle, each drawn at its point (0–4) of the old four steps. */
const round4 = (draw: (step: number) => Frame) =>
  Array.from({ length: FRAMES }, (_, i) => draw((i * 4) / FRAMES));

/** A value over a loop of `FRAMES` frames from keys spread evenly round it, eased between them. */
function loopOf(keys: readonly number[]): number[] {
  return Array.from({ length: FRAMES }, (_, i) => {
    const u = (i / FRAMES) * keys.length;
    const k = Math.floor(u);
    const t = u - k;
    const e = t * t * (3 - 2 * t);
    return keys[k]! + (keys[(k + 1) % keys.length]! - keys[k]!) * e;
  });
}

export function buildCritters(): Record<string, SpriteSet> {
  // Each critter drawn the first time it is asked for.
  return lazyRecord({
    // Idle loops of `FRAMES` frames: a hen looks about and pecks twice; a cat sits, then dozes; a
    // sparrow pecks and hops.
    chicken: () =>
      set(
        loopOf([0, 0, 1, 0, 1, 1]).map((p) => hen(p, 0)),
        round4((k) => hen(0, k)),
        8,
        14,
        14,
      ),
    cat: () => {
      const sit = cat('sit');
      const sleep = cat('sleep');
      return set(
        Array.from({ length: FRAMES }, (_, i) => (i >= 5 && i < 12 ? sleep : sit)),
        round4((k) => cat('walk', k)),
        11,
        15,
        16,
      );
    },
    bird: () =>
      set(
        loopOf([0, 0, 1, 0, 1, 0]).map((p, i) => sparrow(i >= 12 ? 0.6 : 0, p)),
        round4((k) => sparrow(Math.max(0, Math.sin((k * Math.PI) / 2)), 0)),
        5,
        8,
        9,
        round4(sparrowFlying),
      ),
    // The boats on the stream and the pond (lane C0-NPC, shared data/npcLife.ts `LIFE_BOATS`): the C0-PROPS kit's
    // net boat and sampan, a fisherman aboard drawn as himself.
    boatNet: () => boatSet(propSprite('netBoat').img, propSprite('netBoat')),
    boatSampan: () => boatSet(propSprite('sampan').img, propSprite('sampan')),
  });
}

/** A boat as a figure set: the same picture standing and moving, anchored where it sits on the water. */
function boatSet(img: Frame, s: { ax: number; ay: number }): SpriteSet {
  return set([img], [img], s.ax, s.ay, 24);
}
